import {
  createClient,
  type SupabaseClient,
  type User,
} from "@supabase/supabase-js";
import { getMockClient, isMockEnabled, mockUser } from "./mockSupabase";

export { isMockEnabled } from "./mockSupabase";

let client: SupabaseClient | null = null;

// In dev (`vite dev`) the app serves an in-memory mock Supabase so local work
// never touches a real backend. Set VITE_SUPABASE_MOCK=0 to use the real
// project configured below instead. Production builds always hit the backend.
export function isSupabaseConfigured(): boolean {
  return (
    isMockEnabled() ||
    Boolean(
      import.meta.env.VITE_SUPABASE_URL &&
      import.meta.env.VITE_SUPABASE_ANON_KEY
    )
  );
}

export function getSupabase(): SupabaseClient | null {
  if (isMockEnabled()) return getMockClient();
  if (
    !import.meta.env.VITE_SUPABASE_URL ||
    !import.meta.env.VITE_SUPABASE_ANON_KEY
  ) {
    return null;
  }
  client ??= createClient(
    import.meta.env.VITE_SUPABASE_URL as string,
    import.meta.env.VITE_SUPABASE_ANON_KEY as string,
    {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    }
  );
  return client;
}

export interface AuthedClient {
  client: SupabaseClient;
  user: User;
}

// Anonymous-auth sessions: ensure we have a session (anyone can create
// content without sign-up), creating one on first visit when needed.
export async function ensureSession(): Promise<AuthedClient | null> {
  if (isMockEnabled()) {
    const mock = getMockClient();
    return mock ? { client: mock, user: mockUser } : null;
  }

  const sb = getSupabase();
  if (!sb) return null;

  const { data } = await sb.auth.getSession();
  if (data.session?.user) return { client: sb, user: data.session.user };

  const { data: anon } = await sb.auth.signInAnonymously();
  if (anon.session?.user) return { client: sb, user: anon.session.user };
  return null;
}

export async function currentUser(): Promise<User | null> {
  if (isMockEnabled()) return mockUser;

  const sb = getSupabase();
  if (!sb) return null;
  const { data } = await sb.auth.getSession();
  return data.session?.user ?? null;
}
